import axios, { type AxiosInstance } from 'axios';
import { base64guid } from '@seeqdev/utils';
import { APPSERVER_API_CONTENT_TYPE } from './axios.constants';
import { attachLifecycleInterceptors } from './requestLifecycle.utilities';
import { getRequestIdFromHeaders } from './requestUrl.utilities';
import type { AxiosError, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios';
import type { GraphqlOperation } from '../graphql/executeGraphQL';

// Extend Axios request config to support the `silent` flag used by pending-request tracking.
declare module 'axios' {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  interface AxiosRequestConfig<D = any> {
    silent?: boolean;
    /**
     * Path template for the request, e.g. `/api/items/{id}`, used as its telemetry label. Set automatically by the
     * generated SDK; hand-written callers can set it too.
     */
    urlTemplate?: string;
    /**
     * The GraphQL operation this request executes. Set by `executeGraphQL`; request lifecycle
     * observers receive it as is.
     */
    graphqlOperation?: GraphqlOperation;
  }
}

/** Attach pending-request tracking interceptors to a provided axios instance. */
export function attachPendingInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use((value) => {
    if (!value.silent) {
      incrementPendingRequests(getRequestIdFromHeaders(value.headers));
    }
    return value;
  });
  instance.interceptors.response.use(
    (value) => {
      if (!value.config?.silent) {
        decrementPendingRequests(getRequestIdFromHeaders(value.config?.headers));
      }
      return value;
    },
    (error) => {
      if (!error?.config?.silent) {
        decrementPendingRequests(getRequestIdFromHeaders(error?.config?.headers));
      }
      return Promise.reject(error);
    },
  );
}

let axiosInstance: AxiosInstance | null = null;

type CreateAxiosInstanceOptions = {
  /** Attach the pending-request tracking interceptors (see `attachPendingInterceptors`). Defaults to true. */
  withPendingInterceptor?: boolean;
  /** Attach the request lifecycle interceptors (see `attachLifecycleInterceptors`). Defaults to true. */
  withLifecycleInterceptor?: boolean;
  /**
   * Sets axios `baseURL`, so relative request urls resolve against it. A `baseURL` in `config` wins. It may include an
   * origin (`https://example.com`) or just be a path (`/api`); its origin is what this instance's lifecycle events
   * are reported against, in preference to the SDK's. Leave it unset for an instance talking to the SDK's own server.
   */
  baseUrl?: string;
};

/** Create a preconfigured axios instance, with optional pending-request and lifecycle tracking. */
export function createAxiosInstance(
  config?: AxiosRequestConfig,
  options: CreateAxiosInstanceOptions = {},
): AxiosInstance {
  const { withPendingInterceptor = true, withLifecycleInterceptor = true, baseUrl } = options;
  axiosInstance = axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': APPSERVER_API_CONTENT_TYPE,
      'Accept': APPSERVER_API_CONTENT_TYPE,
    },
    ...config,
  });

  axiosInstance.defaults.headers.common['Accept'] = APPSERVER_API_CONTENT_TYPE;

  // Lifecycle first so its request half runs last (after every header is set) and its
  // response half runs first (before any interceptor reshapes the error).
  if (withLifecycleInterceptor) {
    attachLifecycleInterceptors(axiosInstance);
  }
  if (withPendingInterceptor) {
    attachPendingInterceptors(axiosInstance);
  }

  return axiosInstance;
}

/** Get or lazily create the package's default axios instance. */
export function getAxiosInstance(): AxiosInstance {
  if (axiosInstance) {
    return axiosInstance;
  }

  const instance = createAxiosInstance();
  return instance;
}

/** Replace the package's axios instance (assumes the instance is already configured). */
export function setAxiosInstance(instance: AxiosInstance) {
  axiosInstance = instance;
}

/** Reset the stored axios instance (test-only helper). */
export function resetAxiosInstance() {
  axiosInstance = null;
}

/** AxiosRequestConfig extended with Seeq-specific fields. */
export type SeeqAxiosRequestConfig<D = unknown> = {
  /** Cancel group name for Seeq cancellation coordination. */
  cancellationGroup?: string;
  /** Request server-side cancellation. */
  cancelOnServer?: boolean;
  /** Skip the loading bar for this request. */
  ignoreLoadingBar?: boolean;
  /** Marks a request as a refetch for UI/telemetry. */
  refetching?: boolean;
  /** Skip pending-request tracking for this request. */
  silent?: boolean;
} & AxiosRequestConfig<D>;

/** Internal request config shape used by axios interceptors. */
export type SeeqInternalAxiosRequestConfig = {
  headers: AxiosRequestHeaders;
} & SeeqAxiosRequestConfig;

/** Axios response with Seeq-specific request config attached. */
export type SeeqAxiosResponse = {
  config: SeeqInternalAxiosRequestConfig;
} & AxiosResponse;

type PendingRequestsSubscriber = () => void;

const pendingRequests: string[] = [];
const subscribers = new Set<PendingRequestsSubscriber>();

function notifySubscribers() {
  subscribers.forEach((subscriber) => subscriber());
}

/** Read the current in-flight request count. */
export function getPendingRequestsCount() {
  return pendingRequests.length;
}

/** Increment the in-flight request count by storing the request ID. */
export function incrementPendingRequests(requestId?: string) {
  if (!requestId) {
    return;
  }
  pendingRequests.push(requestId);
  notifySubscribers();
}

/** Decrement the in-flight request count by removing the matching request ID. */
export function decrementPendingRequests(requestId?: string) {
  if (!requestId) {
    return;
  }
  const index = pendingRequests.indexOf(requestId);
  if (index !== -1) {
    pendingRequests.splice(index, 1);
    notifySubscribers();
  } else {
    console.warn(
      `Tried to decrement pending requests with ID ${requestId}, but it was not found in the pending requests list.`,
    );
  }
}

/** Check whether a request ID is currently tracked as pending. */
export function isPendingRequest(requestId?: string) {
  if (!requestId) {
    return false;
  }
  return pendingRequests.includes(requestId);
}

/** Reset the in-memory pending request count to zero. */
export function resetPendingRequests() {
  if (pendingRequests.length > 0) {
    pendingRequests.length = 0;
    notifySubscribers();
  }
}

/** Subscribe to pending request count updates. */
export function subscribeToPendingRequests(subscriber: PendingRequestsSubscriber) {
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
}

const HttpCodesBadRequest = 400;

/** Normalize various axios error shapes into a stable Error/message. */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export function extractError(errorOrResponse: any) {
  if (errorOrResponse?.response?.status >= HttpCodesBadRequest) {
    return new Error(errorOrResponse.response.data?.statusMessage || errorOrResponse.response.data);
  }
  if (errorOrResponse.request && errorOrResponse.data?.errors) {
    return new Error(errorOrResponse.data.errors[0].message);
  }
  if (errorOrResponse.request && !errorOrResponse.data && errorOrResponse.message) {
    return new Error(errorOrResponse.message);
  }
  if (errorOrResponse.request && !errorOrResponse.data) {
    return new Error(errorOrResponse.request);
  }
  if (errorOrResponse.config && !errorOrResponse.data) {
    return new Error(errorOrResponse.config);
  }
  return errorOrResponse?.data?.statusMessage || errorOrResponse;
}

/**
 * Generates a request ID used for API tracing.
 *
 * TODO: This will move into a shared utilities package in the future. https://seeq.atlassian.net/browse/CRAB-57828
 */
export function generateRequestId(prefix?: string) {
  return `${prefix ?? ''}R^${base64guid()}`;
}

/** Type guard for axios errors, including fallback on AxiosError name. */
export function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error) || (error as { name?: string })?.name === 'AxiosError';
}
